// A plan run's timeline, read from the runner's own log (GET .../logs/runner):
// the setup phases, every test, and every step of every attempt with its
// verdict and time. The log is written as the run goes, so parsing a partial
// one gives the timeline so far.
//
//   2026-10-13T21:38:21.544Z [phase] running the plan (15 tests, 188 steps)
//   2026-10-13T21:38:22.915Z [step] abilities-off 6/13 command
//   2026-10-13T21:38:22.915Z [step-info] abilities-off 6/13 /gamerule showcoordinates true
//   2026-10-13T21:38:23.029Z [step] abilities-off 6/13 command -> error: command_failed: … (114ms)
//   2026-10-13T21:38:23.030Z [step-seen] abilities-off 7/13 mainhand snst_bckp:ender_backpack
//   2026-10-13T21:38:24.365Z [test] abilities-off -> error after 2 attempts

export interface QaStep {
  index: number; // 0-based
  count: number;
  action: string;
  attempt: number;
  at: number; // ms since epoch
  ms?: number;
  state?: string; // pass | fail | error | captured; absent while it runs
  reason?: string;
  /** What the step does ("/give @s apple", "check the player: mainhand …"), from runners that log it. */
  detail?: string;
  /** What an inspecting step saw when it passed. */
  seen?: string;
}

export interface QaTestTimeline {
  id: string;
  at: number;
  end?: number;
  status?: string;
  summary?: string; // what the [test] line said after the status, e.g. "after 2 attempts"
  attempts: number;
  steps: QaStep[]; // every attempt's, in order
  /** The step that decided a non-pass verdict (the last attempt's last step). */
  failed?: QaStep;
}

export interface QaPhase {
  at: number;
  label: string;
}

export interface QaTimeline {
  phases: QaPhase[];
  tests: QaTestTimeline[];
  /** The wall-clock span the recording covers: the plan's own steps (the runner starts recording right before them). */
  recording: { start: number; end: number } | null;
  start: number | null;
  end: number | null;
}

const LINE = /^(\d{4}-\d\d-\d\dT[\d:.]+Z) \[([\w-]+)\] (.*)$/;
const STEP_START = /^(.+?) (\d+)\/(\d+) (\S+)(?: \(attempt (\d+)\))?$/;
const STEP_END = /^(.+?) (\d+)\/(\d+) (\S+) -> (\w+)(?:: (.*))? \((\d+)ms\)$/;
const STEP_NOTE = /^(.+?) (\d+)\/\d+ (.*)$/;
const TEST_END = /^(.+?) -> (\w+)\s*(.*)$/;

export function parseRunnerLog(text: string): QaTimeline {
  const phases: QaPhase[] = [];
  const tests: QaTestTimeline[] = [];
  const byId = new Map<string, QaTestTimeline>();
  let first: number | null = null;
  let last: number | null = null;
  const testFor = (id: string, at: number) => {
    let t = byId.get(id);
    if (!t) {
      t = { id, at, attempts: 0, steps: [] };
      byId.set(id, t);
      tests.push(t);
    }
    return t;
  };

  for (const raw of text.split(/\r?\n/)) {
    const m = LINE.exec(raw);
    if (!m) continue;
    const at = Date.parse(m[1]!);
    if (Number.isNaN(at)) continue;
    first ??= at;
    last = at;
    const [, , tag, rest] = m as unknown as [string, string, string, string];
    if (tag === "phase") {
      phases.push({ at, label: rest });
    } else if (tag === "step") {
      const end = STEP_END.exec(rest);
      if (end) {
        const t = testFor(end[1]!, at);
        const index = Number(end[2]) - 1;
        const step = [...t.steps].reverse().find((s) => s.index === index && s.state === undefined);
        if (step) {
          step.state = end[5];
          step.ms = Number(end[7]);
          if (end[6]) step.reason = end[6];
        }
        continue;
      }
      const start = STEP_START.exec(rest);
      if (start) {
        const t = testFor(start[1]!, at);
        const attempt = Number(start[5] ?? 1);
        t.attempts = Math.max(t.attempts, attempt);
        t.steps.push({ index: Number(start[2]) - 1, count: Number(start[3]), action: start[4]!, attempt, at });
      }
    } else if (tag === "step-info" || tag === "step-seen") {
      // Each follows its step's own line, so it belongs to the latest step of that index.
      const note = STEP_NOTE.exec(rest);
      const t = note && byId.get(note[1]!);
      const step = t && [...t.steps].reverse().find((s) => s.index === Number(note[2]) - 1);
      if (step) step[tag === "step-info" ? "detail" : "seen"] = note![3];
    } else if (tag === "test") {
      const end = TEST_END.exec(rest);
      if (!end) continue;
      const t = testFor(end[1]!, at);
      t.end = at;
      t.status = end[2];
      if (end[3]) t.summary = end[3];
    }
  }

  for (const t of tests) {
    if (t.status && t.status !== "pass" && t.status !== "captured") {
      const lastAttempt = t.steps.filter((s) => s.attempt === t.attempts);
      t.failed = [...lastAttempt].reverse().find((s) => s.state && s.state !== "pass" && s.state !== "captured") ?? lastAttempt.at(-1);
    }
    t.end ??= t.steps.at(-1) ? t.steps.at(-1)!.at + (t.steps.at(-1)!.ms ?? 0) : undefined;
  }

  const planStart = phases.find((p) => p.label.startsWith("running the plan"))?.at;
  const planEnd = phases.find((p) => p.label.startsWith("stopping BDS"))?.at ?? last;
  return {
    phases,
    tests,
    recording: planStart !== undefined && planEnd !== null && planEnd > planStart ? { start: planStart, end: planEnd } : null,
    start: first,
    end: last,
  };
}

/** Where in the recording (seconds) a moment of the run is, assuming the frames were captured at an even rate. */
export function recordingTime(timeline: QaTimeline, at: number, duration: number): number | null {
  const r = timeline.recording;
  if (!r || !Number.isFinite(duration) || duration <= 0) return null;
  const share = (at - r.start) / (r.end - r.start);
  return Math.min(Math.max(share, 0), 1) * duration;
}

/** "1m 33s", "820ms". */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
}

/** The setup phases with how long each took (until the next phase began). */
export function phaseDurations(timeline: QaTimeline): { label: string; ms: number | null }[] {
  return timeline.phases.map((p, i) => {
    const next = timeline.phases[i + 1]?.at ?? null;
    return { label: p.label, ms: next === null ? null : next - p.at };
  });
}
